import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiGetBlobFetcher } from '@/common/api/fetcher'
import { apiPutFetcher } from '@/common/api/fetcher'
import {
  DispatchResponse,
  DispatchVars,
  GetPreviewVars,
  GetQueueVars,
  GetSenderVars,
  MailPreviewResponse,
  MailQueueResponse,
  MailSenderSettingResponse,
  RetryResponse,
  RetryVars,
  IncentiveCsvVars,
  IncentiveMarkResponse,
  IncentiveMarkVars,
  IncentiveTargetsResponse,
  IncentiveTargetsVars,
  ManualRequestResponse,
  ManualRequestVars,
  TestSendResponse,
  TestSendVars,
  UGCMailApiPath,
  UpdateSenderVars
} from '@/common/api/params/ugcMail'

/** 差出人と送信モード。未設定でも既定値が返るので undefined にはならない */
export const useMailSenderApi = (vars: GetSenderVars | undefined) => {
  return useSWR<MailSenderSettingResponse, Response, [string, GetSenderVars] | undefined>(
    vars ? [UGCMailApiPath.GET_SENDER, vars] : undefined
  )
}

export const useUpdateMailSenderApi = (
  options?: MutationOptions<MailSenderSettingResponse>
) => {
  return useCallMutation<MailSenderSettingResponse, UpdateSenderVars>(
    UGCMailApiPath.UPDATE_SENDER,
    options,
    apiPutFetcher
  )
}

export const useMailQueueApi = (vars: GetQueueVars | undefined) => {
  return useSWR<MailQueueResponse, Response, [string, GetQueueVars] | undefined>(
    vars ? [UGCMailApiPath.GET_QUEUE, vars] : undefined
  )
}

/** 送信待ちを今すぐ送る */
export const useDispatchMailApi = (options?: MutationOptions<DispatchResponse>) => {
  return useCallMutation<DispatchResponse, DispatchVars>(UGCMailApiPath.DISPATCH, options)
}

/** 失敗を送信待ちへ戻す */
export const useRetryMailApi = (options?: MutationOptions<RetryResponse>) => {
  return useCallMutation<RetryResponse, RetryVars>(UGCMailApiPath.RETRY, options)
}

/** 実際に送られる件名・本文（送信と同じ組み立てを通す） */
export const useMailPreviewApi = (vars: GetPreviewVars | undefined) => {
  return useSWR<MailPreviewResponse, Response, [string, GetPreviewVars] | undefined>(
    vars ? [UGCMailApiPath.GET_PREVIEW, vars] : undefined
  )
}

/** 1通だけ試し送り。送信モードが off でも実行できる */
export const useTestSendMailApi = (options?: MutationOptions<TestSendResponse>) => {
  return useCallMutation<TestSendResponse, TestSendVars>(UGCMailApiPath.TEST_SEND, options)
}

/** 個別に1件だけ依頼する（自動生成の対象にならない相手向け） */
export const useCreateManualRequestApi = (options?: MutationOptions<ManualRequestResponse>) => {
  return useCallMutation<ManualRequestResponse, ManualRequestVars>(
    UGCMailApiPath.MANUAL_REQUEST,
    options
  )
}

/** 投稿特典の対象者CSV（W2Commerce側で人が処理するためのリスト） */
export const useDownloadIncentiveCsv = (onSuccess: (blob: Blob) => void) => {
  return useSWRMutation<Blob, Response, string, IncentiveCsvVars>(
    UGCMailApiPath.INCENTIVE_CSV,
    apiGetBlobFetcher,
    { onSuccess }
  )
}

/** 投稿特典の未処理一覧（処理済み管理はシステム側が持つ） */
export const useIncentiveTargetsApi = (vars: IncentiveTargetsVars | undefined) => {
  return useSWR<IncentiveTargetsResponse, Response, [string, IncentiveTargetsVars] | undefined>(
    vars ? [UGCMailApiPath.INCENTIVE_TARGETS, vars] : undefined
  )
}

/** W2側で付与を終えた対象を処理済みにする */
export const useMarkIncentiveProcessedApi = (
  options?: MutationOptions<IncentiveMarkResponse>
) => {
  return useCallMutation<IncentiveMarkResponse, IncentiveMarkVars>(
    UGCMailApiPath.INCENTIVE_MARK,
    options
  )
}
